/**
 * TimelineErrorBoundary Component
 * Error boundary for graceful error handling in timeline chart
 */

import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Typography, Button } from '@mui/material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class TimelineErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Timeline Error:', error);
    console.error('Error Info:', errorInfo);

    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 4,
            minHeight: 200,
            backgroundColor: '#fef2f2',
            borderRadius: 2,
            border: '1px solid #fecaca',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: '#991b1b',
              mb: 2,
              fontWeight: 600,
            }}
          >
            Timeline Error
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#7f1d1d',
              mb: 3,
              textAlign: 'center',
              maxWidth: 500,
            }}
          >
            {this.state.error?.message || 'An unexpected error occurred while rendering the timeline.'}
          </Typography>
          <Button
            variant="contained"
            onClick={this.handleReset}
            sx={{
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c',
              },
            }}
          >
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}
